import { scaleUtc, scaleLinear } from '@visx/scale'
import { extent, bisector } from 'd3-array'
import moment from 'moment'

export const ScaleType = {
  Utc: 'utc',
  Linear: 'linear'
}

const ScaleFactory = {
  [ScaleType.Utc]: scaleUtc,
  [ScaleType.Linear]: scaleLinear
}

class LineChartService {
  /**
   * Build the domains for X and Y
   *
   * @param {Object[]} series Array of series objects with a points array property
   * @param {Object} boundsX min/max bounds for X
   * @param {Object} boundsY min/max bounds for Y
   * @returns {Object} Domains for X and Y
   */
  calculateDomain (series, boundsX = {}, boundsY = {}) {
    const hasBothBounds = (bounds) => bounds.min !== undefined && bounds.max !== undefined
    if (hasBothBounds(boundsX) && hasBothBounds(boundsY)) {
      return {
        x: [boundsX.min, boundsX.max],
        y: [boundsY.min, boundsY.max]
      }
    }

    const { x: valuesX, y: valuesY } = this.getDistinctValues(series)
    const extentX = extent(valuesX)
    const extentY = extent(valuesY)

    return {
      x: [
        boundsX.min ?? extentX[0],
        boundsX.max ?? extentX[1]
      ],
      y: [
        boundsY.min ?? extentY[0],
        boundsY.max ?? extentY[1]
      ]
    }
  }

  /**
   * Iterate over the series and pull out all the distinct x and y values
   * Supports numbers or dates
   *
   * @param {Object[]} series Array of series objects with a points array property
   * @returns {Object} Object with an x and y array of distinct values
   */
  getDistinctValues (series = []) {
    const isDate = {
      x: false,
      y: false
    }
    const convertValue = (value, axis) => {
      if (value instanceof Date) {
        isDate[axis] = true
        return value.getTime()
      }
      return value
    }

    const distinctValues = series.reduce((distinctValues, { points = [], bands = [] }) => {
      points.forEach(({ x, y }) => {
        distinctValues.x.add(convertValue(x, 'x'))
        distinctValues.y.add(convertValue(y, 'y'))
      })
      bands.forEach(({ points = [] }) => {
        points.forEach(({ y0, y1 }) => {
          distinctValues.y.add(convertValue(y0, 'y'))
          distinctValues.y.add(convertValue(y1, 'y'))
        })
      })
      return distinctValues
    }, { x: new Set(), y: new Set() })

    const convertBackToDates = (v) => [...v].map((x) => new Date(x))
    return {
      x: isDate.x ? convertBackToDates(distinctValues.x) : [ ...distinctValues.x ],
      y: isDate.y ? convertBackToDates(distinctValues.y) : [ ...distinctValues.y ]
    }
  }

  /**
   * Build the scale based on the type and provided range & domain
   *
   * @param {string} scaleType Type of the scale to build
   * @param {number[]} domain Array with min and max values
   * @param {number[]} range Array with min and max pixel positions
   */
  buildScale (scaleType, domain, range) {
    const shouldConvertToDate = (domainValue) => {
      domainValue = domainValue ?? undefined
      return scaleType === ScaleType.Utc && domainValue !== undefined && !(domainValue instanceof Date)
    }
    if (shouldConvertToDate(domain?.[0])) {
      domain[0] = moment.utc(domain[0]).toDate()
    }
    if (shouldConvertToDate(domain?.[1])) {
      domain[1] = moment.utc(domain[1]).toDate()
    }
    const result = ScaleFactory[scaleType]({ range, domain })
    return result
  }

  /**
   * Determine if the plot area is wide enough to accommodate points comfortably
   *
   * @param {Object[]} series Array of series objects with a points array property
   * @param {number} width Width of the plot area
   * @returns {boolean}
   */
  shouldShowPoints (series, width) {
    const pointCounts = series?.map((s) => s.points?.length ?? 0) || []
    const maxPointCount = Math.max(0, ...pointCounts)
    const pointWidthPlusMargin = 15
    if (maxPointCount > 0 && (width / maxPointCount) < pointWidthPlusMargin) {
      return false
    }
    return true
  }

  /**
   * Check if the domain has negative numbers
   *
   * @param {Array} domain Domain which is an array of 2 values (could be numbers or dates)
   * @returns {boolean}
   */
  hasNegativeValues (domain = []) {
    const isNegativeNumber = (val) => Number.isFinite(Number(val)) && Number(val) < 0
    return !!domain.find(isNegativeNumber)
  }

  /**
   * Find the closest point in each series to the given x value
   * If there is a point missing on any of the series, it will provide the previous & next points
   *
   * @param {Object[]} series Array of series objects with a points array property
   * @param {*} x X value to find the closest point to
   */
  findClosestPoints (series, x) {
    const closestIndexes = series.map(({ points, name }) => ({ points, name, closestIndex: this.findClosestPointIndex(points, x) }))

    // First find the closest X point across all series
    let currentX = closestIndexes.reduce((currentX, { points, closestIndex }) => {
      const closestXForSeries = points[closestIndex].x
      return (Math.abs(x - closestXForSeries) < Math.abs(x - currentX))
        ? closestXForSeries
        : currentX
    }, Infinity)

    let closestCurrentPoint

    // Then map the point where x == currentX
    // If there is no x at currentX, map prev & next points
    const closestPoints = closestIndexes.map(({ points, name, closestIndex }) => {
      currentX = (currentX instanceof Date) ? currentX.getTime() : currentX
      const closestPointX = (points[closestIndex].x instanceof Date)
        ? points[closestIndex].x.getTime()
        : points[closestIndex].x

      if (closestPointX === currentX) {
        closestCurrentPoint = points[closestIndex]
        return { current: closestCurrentPoint, name }
      } else if (closestPointX < currentX) {
        return {
          previous: points[closestIndex],
          next: (closestIndex < points.length - 1) ? points[closestIndex + 1] : undefined,
          name
        }
      } else {
        return {
          previous: (closestIndex > 0) ? points[closestIndex - 1] : undefined,
          next: points[closestIndex],
          name
        }
      }
    })

    return { closestPoints, closestCurrentPoint }
  }

  /**
   * Find the closest point in a series of points given an x value
   *
   * @param {Object[]} points Array of point objects with x property
   * @param {*} x X value to find the closest point to
   */
  findClosestPointIndex (points, x) {
    const bisect = bisector((p) => p.x).right
    let closestPointIndex = bisect(points, x, 1)

    if (closestPointIndex >= points.length) {
      // x is to the right of the right-most point - use right-most index
      return points.length - 1
    }

    if (closestPointIndex > 0) {
      // check if x is closer to the point on the left or the right
      const left = points[closestPointIndex - 1].x
      const right = points[closestPointIndex].x
      if ((x - left) < (right - x)) {
        closestPointIndex = closestPointIndex - 1
      }
    }
    return closestPointIndex
  }
}

export const lineChartService = new LineChartService()
